import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export type MenuMode = 'static' | 'overlay' | 'horizontal' | 'slim' | 'slim-plus' | 'reveal' | 'drawer';

export type ColorScheme = 'light' | 'dark';

export type TopbarColorScheme = 'light' | 'dark'| 'transparent';

export interface AppConfig {
    inputStyle: string;
    colorScheme: ColorScheme;
    theme: string;
    ripple: boolean;
    menuMode: MenuMode;
    scale: number;
    menuTheme: ColorScheme;
    topbarTheme: TopbarColorScheme;
}

/** A FitZone theme: a compiled PrimeNG theme (assets/layout/styles/theme/fz/<id>) plus the tokens in src/fz-theme.scss. */
export interface FzTheme {
    id: string;
    label: string;
    mode: ColorScheme;
    /** Ground, surface and accent, for the picker's preview. */
    swatch: [string, string, string];
}

export const FZ_THEMES: FzTheme[] = [
    { id: 'chalk', label: 'Chalk', mode: 'light', swatch: ['#eff2f0', '#ffffff', '#0f8a7c'] },
    { id: 'ocean', label: 'Ocean', mode: 'light', swatch: ['#ecf1f8', '#ffffff', '#2563eb'] },
    { id: 'lilac', label: 'Lilac', mode: 'light', swatch: ['#f2f0f8', '#ffffff', '#6d4ae0'] },
    { id: 'rubber', label: 'Rubber', mode: 'dark', swatch: ['#171a1f', '#21252c', '#2dd4bf'] },
    { id: 'midnight', label: 'Midnight', mode: 'dark', swatch: ['#0d1428', '#172039', '#8b9cff'] },
    { id: 'ember', label: 'Ember', mode: 'dark', swatch: ['#1b1513', '#271f1b', '#ff8a3d'] }
];

const THEME_KEY = 'fz-theme';

interface LayoutState {
    staticMenuDesktopInactive: boolean;
    overlayMenuActive: boolean;
    profileSidebarVisible: boolean;
    configSidebarVisible: boolean;
    staticMenuMobileActive: boolean;
    menuHoverActive: boolean;
    topbarMenuActive: boolean;
    sidebarActive: boolean;
    anchored: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class LayoutService {

    config: AppConfig = {
        ripple: false,
        inputStyle: 'outlined',
        menuMode: 'static',
        colorScheme: 'light',
        theme: 'chalk',
        scale: 14,
        menuTheme: 'light',
        topbarTheme: 'transparent'
    };

    state: LayoutState = {
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        profileSidebarVisible: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false,
        sidebarActive: false,
        topbarMenuActive: false,
        anchored: false
    };

    private configUpdate = new Subject<AppConfig>();

    private overlayOpen = new Subject<any>();

    private topbarMenuOpen = new Subject<any>();

    topbarMenuOpen$ = this.topbarMenuOpen.asObservable();

    configUpdate$ = this.configUpdate.asObservable();

    overlayOpen$ = this.overlayOpen.asObservable();

    onMenuToggle() {
        if (this.isOverlay()) {
            this.state.overlayMenuActive = !this.state.overlayMenuActive;

            if (this.state.overlayMenuActive) {
                this.overlayOpen.next(null);
            }
        }

        if (this.isDesktop()) {
            this.state.staticMenuDesktopInactive = !this.state.staticMenuDesktopInactive;
        } else {
            this.state.staticMenuMobileActive = !this.state.staticMenuMobileActive;

            if (this.state.staticMenuMobileActive) {
                this.overlayOpen.next(null);
            }
        }
    }

    onTopbarMenuToggle() {
        this.state.topbarMenuActive = !this.state.topbarMenuActive;
        if (this.state.topbarMenuActive) {
            this.topbarMenuOpen.next(null);
        }
    }

    onOverlaySubmenuOpen() {
        this.overlayOpen.next(null);
    }

    showSidebar() {
        this.state.profileSidebarVisible = true;
    }

    showConfigSidebar() {
        this.state.configSidebarVisible = true;
    }

    isOverlay() {
        return this.config.menuMode === 'overlay';
    }

    isDesktop() {
        return window.innerWidth > 991;
    }

    isSlim() {
        return this.config.menuMode === 'slim';
    }

    isSlimPlus() {
        return this.config.menuMode === 'slim-plus';
    }

    isHorizontal() {
        return this.config.menuMode === 'horizontal';
    }

    isMobile() {
        return !this.isDesktop();
    }

    onConfigUpdate() {
        this.configUpdate.next(this.config);
    }

    readonly themes = FZ_THEMES;

    /** The theme index.html applied before the app started (from the saved choice, or the device's light/dark setting). */
    themeId = document.documentElement.dataset['fzTheme'] ?? 'chalk';

    constructor() {
        const mode = this.themeOf(this.themeId).mode;
        this.config.colorScheme = mode;
        this.config.menuTheme = mode;
    }

    private themeOf(id: string): FzTheme {
        return FZ_THEMES.find(t => t.id === id) ?? FZ_THEMES[0];
    }

    /**
     * Switches theme: loads the new compiled theme next to the old one and drops the old one once loaded, so components
     * are never unstyled, then cross-fades the page where the browser supports view transitions.
     */
    applyTheme(id: string) {
        const theme = this.themeOf(id);
        const link = document.getElementById('theme-link') as HTMLLinkElement | null;
        const href = `assets/layout/styles/theme/fz/${theme.id}/theme.css`;
        try {
            localStorage.setItem(THEME_KEY, theme.id);
        } catch {
            // Private mode or blocked storage: the theme still applies for this visit.
        }

        const commit = () => {
            document.documentElement.dataset['fzTheme'] = theme.id;
            this.themeId = theme.id;
            this.config.theme = theme.id;
            this.config.colorScheme = theme.mode;
            this.config.menuTheme = theme.mode;
            this.onConfigUpdate();
        };

        const swap = () => new Promise<void>(resolve => {
            if (!link || link.getAttribute('href') === href) {
                commit();
                return resolve();
            }
            const next = link.cloneNode() as HTMLLinkElement;
            next.id = 'theme-link-next';
            next.href = href;
            const done = () => {
                link.remove();
                next.id = 'theme-link';
                commit();
                resolve();
            };
            next.addEventListener('load', done, { once: true });
            next.addEventListener('error', done, { once: true });
            link.after(next);
        });

        const doc = document as Document & { startViewTransition?: (update: () => Promise<void>) => unknown };
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (doc.startViewTransition && !reduced) {
            doc.startViewTransition(swap);
        } else {
            swap();
        }
    }
}
